// TODO(afontcu): We should find a way to exclude expected errors without having to rely
// on the `EXCLUDE_FROM_SENTRY` symbol + beforeSend hack.

import filters from 'src/internal/errors/constants/filters';
import {extractErrorMessage} from 'src/internal/errors/sentry/utils';

import type {EventHint} from '@sentry/types';
import type {SentryEvent} from 'src/internal/errors/sentry/types';

export function getFutureEvent<T extends SentryEvent>(
  event: T,
  hint?: EventHint | undefined,
): T | null {
  const transformedEvent = removeExcludedEvents(event, hint);
  return removeFilteredEventsByExtraData(transformedEvent);
}

export function beforeSend<T extends SentryEvent>(
  event: T,
  hint?: EventHint | undefined,
): T | null {
  const filteredEvent = getFutureEvent(event, hint);

  return filteredEvent ? setFingerprint(filteredEvent) : null;
}

/**
 * Adding this symbol to an error will cause it to not be logged to Sentry. Use at your own risk.
 */
export const EXCLUDE_FROM_SENTRY = Symbol.for(
  'ExpectedErrorDoesNotLogToSentry',
);

const removeExcludedEvents = <T extends SentryEvent>(
  event: T,
  hint: EventHint | undefined,
): T | null => {
  if (!event.exception || !event.exception.values?.length) {
    return event;
  }

  // @ts-expect-error TS2339: Property 'graphQLErrors' does not exist on type 'Error'
  const graphQLErrors = hint?.originalException?.graphQLErrors ?? [];

  const expectedErrorMessages = graphQLErrors.reduce((acc: any, error: any) => {
    if (error[EXCLUDE_FROM_SENTRY]) {
      acc.push(error.message);
    }
    return acc;
  }, []);

  // If there are any expected errors, remove it/them from values
  if (expectedErrorMessages?.length) {
    event.exception.values = event.exception.values.filter(
      (eventValue) => !expectedErrorMessages.includes(eventValue.value),
    );
  }

  // If after removing we still have errors, return the event. Otherwise, drop it
  return event.exception.values.length ? event : null;
};

const removeFilteredEventsByExtraData = <T extends SentryEvent>(
  event: T | null,
): T | null => {
  if (!event) {
    return event;
  }

  return filters.ignoreErrorsByExtraData.some((ignoreError) =>
    ignoreError(event.extra),
  )
    ? null
    : event;
};

const setFingerprint = <T extends SentryEvent>(event: T): T => {
  event.fingerprint = ['{{ default }}', extractErrorMessage(event)].filter(
    (value) => value != null,
  ) as Array<string>;

  return event;
};
